import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FieldValuesDataSource } from '../../logic/services/field-values-data-source';
import { take } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { ButtonComponent, DialogWrapperComponent, InfoBarComponent, InfoBarType } from '@vdms-hq/ui';

import { FieldValueViewModel } from '@vdms-hq/selectors';
import { buildFieldValuesGraph, FieldValueGraphNode } from '../../logic/field-values-graph-builder';

@Component({
  selector: 'vdms-hq-field-values-tree-dialog',
  templateUrl: './field-values-tree-dialog.component.html',
  styleUrls: ['./field-values-tree-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DialogWrapperComponent, ButtonComponent, TranslatePipe, NgTemplateOutlet, InfoBarComponent],
})
export class FieldValuesTreeDialogComponent implements OnInit {
  readonly #dialogRef = inject(MatDialogRef<FieldValuesTreeDialogComponent>);
  readonly #fieldsDataSource = inject(FieldValuesDataSource);
  readonly data = inject<{ option: FieldValueViewModel }>(MAT_DIALOG_DATA);

  readonly tree = signal<FieldValueGraphNode | null>(null);
  readonly collapsedNodeUuids = signal<Set<string>>(new Set<string>());
  readonly InfoBarType = InfoBarType;

  public close(): void {
    this.#dialogRef.close();
  }

  public isNodeCollapsed(uuid: string): boolean {
    return this.collapsedNodeUuids().has(uuid);
  }

  public toggleNode(uuid: string): void {
    this.collapsedNodeUuids.update((collapsedNodeUuids) => {
      const nextCollapsedNodeUuids = new Set(collapsedNodeUuids);
      if (nextCollapsedNodeUuids.has(uuid)) {
        nextCollapsedNodeUuids.delete(uuid);
      } else {
        nextCollapsedNodeUuids.add(uuid);
      }
      return nextCollapsedNodeUuids;
    });
  }

  ngOnInit(): void {
    this.#fieldsDataSource.allData$.pipe(take(1)).subscribe((fieldValues) => {
      const graph = buildFieldValuesGraph(fieldValues, this.data.option);
      this.tree.set(graph?.root ?? null);
      this.collapsedNodeUuids.set(new Set<string>());
    });
  }
}
